/* eslint-disable react/no-unknown-property -- Firebolt consumes the custom css prop at build time. */
import { css } from '@firebolt-dev/css'
import { useEffect, useRef, useState } from 'react'
import { EarthIcon, HammerIcon, MapPinIcon, MessageSquareTextIcon, MicIcon, MicOffIcon, Music4Icon, UsersIcon } from 'lucide-react'

import { SettingsIcon } from './Icons'
import { ChatScreen } from './ChatScreen'
import { VoiceScreen } from './VoiceScreen'
import { VoiceHealthScreen } from './VoiceHealthScreen'
import { QuestJournalScreen } from './QuestJournalScreen'
import { PartyLobbyScreen } from './PartyLobbyScreen'
import { GuestSecurityScreen } from './GuestSecurityScreen'
import { WalletPlotsScreen } from './WalletPlotsScreen'
import { InventoryScreen } from './InventoryScreen'
import { SettingsScreen } from './SettingsScreen'
import { EmoteWheelScreen } from './EmoteWheelScreen'
import { MiaCompanionScreen } from './MiaCompanionScreen'
import { MarketplaceScreen } from './MarketplaceScreen'
import { EconomyScreen } from './EconomyScreen'
import { SocialMapScreen } from './SocialMapScreen'
import { cls } from './cls'
import { isTouch } from '../utils'
import { zoneAt } from '../island-zones'
import { uuid } from '../../core/utils'
import { TIERS, TIER_LABELS, ensureGraphicsTier, setGraphicsTier } from '../graphics-tier'
import { groupByCategory, searchCommands, toArgs } from '../command-catalog'
import { buildPresenceList, presenceActions } from '../presence'
import { contextActions, contextTitle, detectContext } from '../context-card'
import { buildMapIndex, searchMap } from '../map-search'
import { ART_MODES, loadArtMode, setArtMode } from '../art-mode'
import { BuildMode } from '../../building/BuildMode'
import { createWorldBuildAdapter } from '../../building/commands'
import { subscribeScreenData, walletPlots } from '../screen-data'

// Быстрые эмоции: те же ассеты, что на мировой доске «ЭМОЦИИ» (world/apps/emotes.js).
// Быстрые перемещения по ключевым точкам мира. rotationY 0 = взгляд на юг (-Z).
// Команды грид-слоя (src/core/grid/commands.js). Кнопка шлёт команду на сервер,
// ответ приходит в чат — как в вьюере OpenSim.
const HUD_GRID_COMMANDS = [
  { id: 'help', label: 'ПОМОЩЬ', args: ['help'] },
  { id: 'objects', label: 'МОИ ВЕЩИ В МИРЕ', args: ['objects'] },
  { id: 'take', label: 'ЗАБРАТЬ ВЕЩЬ', args: ['take'] },
  { id: 'home', label: 'ДОМОЙ', args: ['home'] },
  { id: 'sethome', label: 'ДОМ ЗДЕСЬ', args: ['sethome'] },
  { id: 'lm', label: 'ЗАКЛАДКИ', args: ['lm'] },
  { id: 'profile', label: 'ПРОФИЛЬ', args: ['profile'] },
  { id: 'stipend', label: 'ВЫПЛАТА', args: ['stipend'] },
]

const HUD_PLACES = [
  { id: 'street', label: 'УЛИЦА · СПАВН', position: [-35, 1.7, -208], rotationY: -Math.PI / 2 },
  { id: 'plots-west', label: 'УЧАСТКИ · ЗАПАД', position: [-62, 1.7, -202], rotationY: Math.PI / 2 },
  { id: 'plots-east', label: 'УЧАСТКИ · ВОСТОК', position: [62, 1.7, -202], rotationY: -Math.PI / 2 },
]

const getOnlineCount = world => {
  let count = 0
  world.entities.players.forEach(() => {
    count += 1
  })
  return Math.max(1, count)
}

// Название текущего места по позиции. Список зон и правило выбора живут в
// чистом модуле island-zones.js — так их сверяет тест с местами карты.
const readPosition = player => ({
  x: Number(player?.base?.position?.x ?? player?.data?.position?.[0] ?? 0),
  z: Number(player?.base?.position?.z ?? player?.data?.position?.[2] ?? 0),
})

const getLocation = player => {
  const { x, z } = readPosition(player)
  return { ...zoneAt(x, z), worldId: 'TESTCS // EU-01' }
}

// Компас цели: куда идти прямо сейчас. На острове прибытия ведём к ближайшей
// арке, на острове-спутнике — к порталу возврата, в открытом море — к маяку.
// Данные только живые: позиция игрока и координаты, по которым мир построен.
const ARCH_DISTANCE = 38
const ARCHES = [
  { label: 'ТОРГОВЫЙ КРУГ', x: ARCH_DISTANCE, z: 0 },
  { label: 'ТИХАЯ РОЩА', x: 0, z: ARCH_DISTANCE },
  { label: 'ПОЛИГОН', x: -ARCH_DISTANCE, z: 0 },
]
const ISLAND_RETURN = [
  { at: { cx: 200, cz: 0 }, target: { x: 200, z: -33 } },
  { at: { cx: 0, cz: 200 }, target: { x: 0, z: 167 } },
  { at: { cx: -200, cz: 0 }, target: { x: -200, z: -33 } },
]

const getObjective = player => {
  const { x, z } = readPosition(player)
  const onSpawn = Math.hypot(x, z) <= 56
  if (onSpawn) {
    const nearest = ARCHES.map(arch => ({ ...arch, dist: Math.hypot(x - arch.x, z - arch.z) })).sort((a, b) => a.dist - b.dist)[0]
    return { label: nearest.label, kind: 'арка', dx: nearest.x - x, dz: nearest.z - z, distance: Math.round(nearest.dist) }
  }
  const island = ISLAND_RETURN.find(item => Math.hypot(x - item.at.cx, z - item.at.cz) <= 50)
  const target = island ? island.target : { x: 0, z: 0 }
  return {
    label: island ? 'ОСТРОВ ПРИБЫТИЯ' : 'МАЯК ПРИБЫТИЯ',
    kind: island ? 'портал' : 'путь домой',
    dx: target.x - x,
    dz: target.z - z,
    distance: Math.round(Math.hypot(target.x - x, target.z - z)),
  }
}

export function TestcsHUD({ world, ui, player }) {
  const [online, setOnline] = useState(() => getOnlineCount(world))
  const [voice, setVoice] = useState(() => ({ ...world.livekit.status }))
  const [location, setLocation] = useState(() => getLocation(player))
  const [objective, setObjective] = useState(() => getObjective(player))
  const [buildOpen, setBuildOpen] = useState(false)
  const [entryHintVisible, setEntryHintVisible] = useState(true)
  const [buildAdapter] = useState(() => createWorldBuildAdapter({ world }))
  const [quickPane, setQuickPane] = useState(null)
  const [chatTargetUserId, setChatTargetUserId] = useState('')
  const [landBuildAllowed, setLandBuildAllowed] = useState(false)
  // арт-режим оболочки: аниме (браузер) или реал (заготовка под Steam)
  const [artMode, setArtModeState] = useState(() => loadArtMode())
  const [graphics, setGraphics] = useState(null)
  const [paletteQuery, setPaletteQuery] = useState('')
  const [worldContext, setWorldContext] = useState(null)
  const activePane = ui.active ? ui.pane : null
  const buildAllowed = Boolean(player?.isBuilder?.() || player?.isAdmin?.() || landBuildAllowed)
  const adminAllowed = Boolean(player?.isAdmin?.())
  const locationCopy = location
  // Стрелка компаса на экране: угол между взглядом камеры и направлением на
  // цель. Камера смотрит в (-sin θ, 0, -cos θ), поэтому её курс считается так,
  // а не по «сырому» yaw — иначе стрелка врёт на пол-оборота.
  const cameraYaw = Number(player?.cam?.rotation?.y ?? 0)
  const heading = Math.atan2(-Math.sin(cameraYaw), -Math.cos(cameraYaw))
  const bearing = objective ? Math.atan2(objective.dx, objective.dz) : 0
  const objectiveAngleDeg = Math.round(((heading - bearing) * 180) / Math.PI)

  useEffect(() => {
    const timer = setTimeout(() => setEntryHintVisible(false), 8500)
    return () => clearTimeout(timer)
  }, [])

  useEffect(() => {
    const update = () => {
      const state = walletPlots(world)
      const owned = state.plots.some(plot => plot.mine)
      const granted = state.landPermissions.access.some(item => item.capabilities.some(capability => ['build.place', 'build.edit', 'build.delete'].includes(capability)))
      setLandBuildAllowed(owned || granted)
    }
    update()
    return subscribeScreenData(world, 'wallet', update)
  }, [world])

  // Качество графики подбирается под устройство при первом входе, чтобы мир
  // открывался и на слабой машине. Выбор игрока сохраняется и сильнее авто.
  useEffect(() => {
    try {
      setGraphics(ensureGraphicsTier(world))
    } catch (err) {
      console.error('[graphics] автоподбор не удался', err)
    }
  }, [world])

  useEffect(() => {
    const updateOnline = () => setOnline(getOnlineCount(world))
    const updateVoice = status => setVoice({ ...status })

    world.entities.on('added', updateOnline)
    world.entities.on('removed', updateOnline)
    world.livekit.on('status', updateVoice)
    return () => {
      world.entities.off('added', updateOnline)
      world.entities.off('removed', updateOnline)
      world.livekit.off('status', updateVoice)
    }
  }, [player, world])

  useEffect(() => {
    const updateLocation = () => {
      setLocation(current => {
        const next = getLocation(player)
        return current?.world === next.world ? current : next
      })
      // Компас обновляем чаще состояния зоны: он показывает живую дистанцию.
      setObjective(current => {
        const next = getObjective(player)
        if (current && current.label === next.label && current.distance === next.distance) return current
        return next
      })
    }
    updateLocation()
    const timer = setInterval(updateLocation, 400)
    return () => clearInterval(timer)
  }, [player])

  const togglePane = pane => world.ui.togglePane(pane)
  const toggleQuickPane = pane => setQuickPane(current => (current === pane ? null : pane))
  // Отправляет грид-команду тем же путём, что и «/команда» из чата.
  const runGridCommand = args => {
    world.network?.send('command', { cmd: args[0], value: args.slice(1).join(' '), args, requestId: uuid() })
    world.emit('sidebar-chat-toggle')
    setQuickPane(null)
  }
  const toggleBuild = event => {
    event?.currentTarget?.blur?.()
    if (!buildAllowed) {
      world.emit('toast', 'Build Mode доступен строителям и администраторам')
      return
    }
    setBuildOpen(value => !value)
  }

  useEffect(() => {
    if (!buildAllowed) setBuildOpen(false)
  }, [buildAllowed])

  // Контекст вокруг игрока: участок под ногами или объект рядом. Карточка
  // обновляется сама — мир подсказывает, команды знать не нужно.
  useEffect(() => {
    const read = () => {
      try {
        const localPlayer = world.entities?.player
        if (!localPlayer) return setWorldContext(null)
        const plotOrigins = world.blueprints?.get?.('testcs-build-authority')?.props?.plotOrigins || []
        const props = []
        world.entities?.items?.forEach?.(entity => {
          if (!String(entity.blueprint?.id || '').startsWith('grid-prop-')) return
          const position = entity.root?.position
          if (position) props.push({ name: entity.blueprint?.name?.replace('Инвентарный объект: ', '') || 'Объект', position })
        })
        setWorldContext(current => {
          const next = detectContext({ position: localPlayer.base?.position, plotOrigins, props })
          return JSON.stringify(next) === JSON.stringify(current) ? current : next
        })
      } catch (err) {
        setWorldContext(null)
      }
    }
    read()
    const timer = setInterval(read, 600)
    return () => clearInterval(timer)
  }, [world])

  // «/» открывает палитру команд — привычка из редакторов и мессенджеров.
  useEffect(() => {
    const onKey = event => {
      if (event.key !== '/' || event.repeat) return
      const tag = document.activeElement?.tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA' || document.activeElement?.isContentEditable) return
      event.preventDefault()
      setQuickPane('commands')
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  // /sit — сесть на ближайший объект. Команда клиентская: поза (эмоция) живёт
  // на клиенте, поэтому сервер её включить не может. Шаг отменяет позу сам.
  useEffect(() => {
    const notify = text => world.chat.add({ id: uuid(), from: null, fromId: null, body: text, createdAt: new Date().toISOString() }, false)
    world.chat.bindCommand('sit', () => {
      const localPlayer = world.entities?.player
      if (!localPlayer) return
      const from = localPlayer.base.position
      let best = null
      world.entities.items.forEach(entity => {
        const blueprintId = String(entity.blueprint?.id || '')
        if (!blueprintId.startsWith('grid-prop-') || !entity.root) return
        const position = entity.root.position
        const meters = Math.hypot(position.x - from.x, position.z - from.z)
        if (meters <= 6 && (!best || meters < best.meters)) best = { entity, position, meters }
      })
      if (!best) return notify('Рядом нет объекта, на который можно сесть (6 м).')
      const scale = best.entity.data?.scale?.[1] || 1
      const seatY = best.position.y + 0.45 * scale
      localPlayer.teleport({ position: [best.position.x, seatY, best.position.z], rotationY: localPlayer.base.rotation.y })
      notify('Ты сел(а). Чтобы встать — просто шагни.')
    })
  }, [world])

  useEffect(() => {
    const onKeyDown = event => {
      if (event.code !== 'KeyB' || event.ctrlKey || event.metaKey || event.altKey) return
      if (event.target?.closest?.('input, textarea, select, button, [contenteditable="true"], [role="textbox"]')) return
      if (!buildAllowed || event.repeat) return
      event.preventDefault()
      event.stopImmediatePropagation()
      setBuildOpen(value => !value)
    }
    window.addEventListener('keydown', onKeyDown, { capture: true })
    return () => window.removeEventListener('keydown', onKeyDown, { capture: true })
  }, [buildAllowed])

  // Панели по одной клавише: игрок жаловался, что с ПК «непонятно, что делать»
  // — теперь у каждой кнопки дока есть клавиша, и она подписана на самой
  // кнопке. WASD, Shift, Space и занятые B/G не трогаем.
  useEffect(() => {
    const PANEL_KEYS = { KeyP: 'presence', KeyM: 'map', KeyC: 'avatar', KeyU: 'more' }
    const onPanelKey = event => {
      const pane = PANEL_KEYS[event.code]
      if (!pane || event.repeat || event.ctrlKey || event.metaKey || event.altKey) return
      if (event.target?.closest?.('input, textarea, select, [contenteditable="true"], [role="textbox"]')) return
      event.preventDefault()
      setQuickPane(current => (current === pane ? null : pane))
    }
    window.addEventListener('keydown', onPanelKey, { capture: true })
    return () => window.removeEventListener('keydown', onPanelKey, { capture: true })
  }, [])

  // Hold G to open the radial selector; EmoteWheelScreen commits the current
  // selection on release. Inputs keep the key for typing.
  useEffect(() => {
    const openEmotes = event => {
      if (event.code !== 'KeyG' || event.repeat || event.ctrlKey || event.metaKey || event.altKey) return
      if (event.target?.closest?.('input, textarea, select, [contenteditable="true"], [role="textbox"]')) return
      event.preventDefault()
      setQuickPane('emotes')
    }
    window.addEventListener('keydown', openEmotes, { capture: true })
    return () => window.removeEventListener('keydown', openEmotes, { capture: true })
  }, [])

  return (
    <div
      className={cls('testcs-hud', { 'build-active': buildOpen })}
      css={css`
        --testcs-accent: #9dff63;
        --testcs-accent-soft: rgba(157, 255, 99, 0.16);
        --testcs-panel: rgba(7, 11, 12, 0.8);
        --testcs-border: rgba(255, 255, 255, 0.12);
        position: absolute;
        inset: 0;
        color: #f5f7f2;
        font-size: 1rem;
        pointer-events: none;
        z-index: 2;

        .testcs-topbar {
          position: absolute;
          top: calc(1.25rem + env(safe-area-inset-top));
          left: calc(1.25rem + env(safe-area-inset-left));
          right: calc(1.25rem + env(safe-area-inset-right));
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 1rem;
        }

        .testcs-quickpane {
          position: absolute;
          bottom: calc(6.4rem + env(safe-area-inset-bottom));
          left: 50%;
          transform: translateX(-50%);
          pointer-events: auto;
          background: var(--testcs-panel);
          border: 1px solid var(--testcs-border);
          border-radius: 0.9rem;
          box-shadow: 0 1rem 3rem rgba(0, 0, 0, 0.3);
          backdrop-filter: blur(18px) saturate(130%);
          padding: 0.7rem 0.8rem 0.8rem;
          max-width: min(30rem, calc(100vw - 2rem));
        }
        .testcs-quickpane-title {
          font-size: 0.6rem;
          letter-spacing: 0.14em;
          opacity: 0.72;
          margin-bottom: 0.55rem;
          text-align: center;
        }
        .testcs-quickpane-grid {
          display: flex;
          flex-wrap: wrap;
          justify-content: center;
          gap: 0.45rem;
        }
        .testcs-quickpane-btn {
          pointer-events: auto;
          font: inherit;
          font-size: 0.7rem;
          letter-spacing: 0.06em;
          color: #f5f7f2;
          background: rgba(255, 255, 255, 0.06);
          border: 1px solid var(--testcs-border);
          border-radius: 0.55rem;
          padding: 0.5rem 0.75rem;
          cursor: pointer;
          transition: background 0.15s ease, border-color 0.15s ease;
        }
        .testcs-quickpane-btn:hover {
          background: rgba(255, 255, 255, 0.14);
        }
        .testcs-quickpane-btn.active {
          background: var(--testcs-accent-soft);
          border-color: var(--testcs-accent);
          color: var(--testcs-accent);
        }
        .testcs-quickpane-btn.stop {
          border-color: rgba(255, 173, 112, 0.55);
          color: #ffad70;
        }

        .testcs-brand,
        .testcs-session,
        .testcs-dock,
        .testcs-voice,
        .testcs-help,
        .testcs-entry-hint {
          background: var(--testcs-panel);
          border: 1px solid var(--testcs-border);
          box-shadow: 0 1rem 3rem rgba(0, 0, 0, 0.24);
          backdrop-filter: blur(18px) saturate(130%);
        }

        .testcs-brand {
          min-width: 15.5rem;
          min-height: 3.65rem;
          padding: 0.65rem 0.85rem;
          display: flex;
          align-items: center;
          gap: 0.75rem;
          border-radius: 0.8rem;
        }

        .testcs-mark {
          width: 2.15rem;
          height: 2.15rem;
          position: relative;
          border: 1px solid rgba(157, 255, 99, 0.7);
          background: linear-gradient(145deg, rgba(157, 255, 99, 0.2), rgba(157, 255, 99, 0.02));
          box-shadow: inset 0 0 1rem rgba(157, 255, 99, 0.08);
          transform: rotate(45deg);
          flex: 0 0 auto;
          &::before,
          &::after {
            content: '';
            position: absolute;
            background: var(--testcs-accent);
          }
          &::before {
            width: 0.72rem;
            height: 1px;
            top: 0.66rem;
            left: 0.7rem;
          }
          &::after {
            width: 1px;
            height: 0.72rem;
            top: 0.7rem;
            left: 1.02rem;
          }
        }

        .testcs-brand-copy {
          display: grid;
          gap: 0.12rem;
        }

        .testcs-brand-title {
          font-size: 0.92rem;
          font-weight: 700;
          letter-spacing: 0.18em;
          line-height: 1;
        }

        .testcs-brand-meta {
          color: rgba(245, 247, 242, 0.52);
          font-size: 0.58rem;
          letter-spacing: 0.16em;
          text-transform: uppercase;
        }

        .testcs-session {
          min-height: 3.65rem;
          padding: 0.68rem 0.9rem;
          border-radius: 0.8rem;
          display: flex;
          align-items: center;
          gap: 1rem;
        }

        .testcs-session-main {
          display: grid;
          gap: 0.16rem;
          text-align: right;
        }

        .testcs-session-world {
          font-size: 0.7rem;
          font-weight: 650;
          letter-spacing: 0.12em;
        }

        .testcs-session-id {
          color: rgba(245, 247, 242, 0.48);
          font-size: 0.58rem;
          letter-spacing: 0.12em;
        }

        .testcs-online {
          min-width: 3.2rem;
          padding-left: 0.85rem;
          border-left: 1px solid var(--testcs-border);
          display: grid;
          gap: 0.12rem;
        }

        .testcs-online strong {
          font-size: 0.82rem;
          font-weight: 650;
        }

        .testcs-online span {
          color: var(--testcs-accent);
          font-size: 0.52rem;
          letter-spacing: 0.12em;
        }

        .testcs-objective {
          position: absolute;
          top: calc(5.65rem + env(safe-area-inset-top));
          left: 50%;
          transform: translateX(-50%);
          min-width: 18rem;
          padding: 0.48rem 0.7rem;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.55rem;
          border-top: 1px solid rgba(157, 255, 99, 0.35);
          background: linear-gradient(
            90deg,
            transparent,
            rgba(7, 11, 12, 0.82) 20%,
            rgba(7, 11, 12, 0.82) 80%,
            transparent
          );
          color: rgba(245, 247, 242, 0.72);
          font-size: 0.56rem;
          letter-spacing: 0.14em;
          text-transform: uppercase;
        }

        .testcs-objective strong {
          color: var(--testcs-accent);
          font-size: 0.58rem;
        }

        /* Цель прогулки: единственная плашка, отвечающая «куда идти».
           Держим её у правого края под шапкой, чтобы не спорить с чатом. */
        .testcs-objective {
          position: absolute;
          top: calc(4.35rem + env(safe-area-inset-top));
          right: calc(1rem + env(safe-area-inset-right));
          /* без явной ширины плашка растягивалась по всей строке шапки */
          width: max-content;
          max-width: 16rem;
          display: flex;
          align-items: center;
          gap: 0.6rem;
          padding: 0.5rem 0.8rem;
          border-radius: 0.72rem;
          background: var(--testcs-panel);
          border: 1px solid var(--testcs-border);
          box-shadow: 0 1rem 3rem rgba(0, 0, 0, 0.24);
          backdrop-filter: blur(18px) saturate(130%);
          pointer-events: none;
        }

        .testcs-objective-arrow {
          font-size: 0.95rem;
          line-height: 1;
          color: var(--testcs-accent);
          text-shadow: 0 0 0.7rem rgba(0, 224, 255, 0.55);
          transition: transform 0.12s linear;
        }

        .testcs-objective-copy {
          display: grid;
          gap: 0.12rem;
        }

        .testcs-objective-copy strong {
          font-size: 0.68rem;
          letter-spacing: 0.12em;
          color: rgba(245, 247, 242, 0.94);
        }

        .testcs-objective-copy small {
          font-size: 0.56rem;
          letter-spacing: 0.08em;
          color: rgba(245, 247, 242, 0.6);
        }

        .testcs-entry-hint {
          position: absolute;
          top: calc(7.55rem + env(safe-area-inset-top));
          left: 50%;
          transform: translateX(-50%);
          max-width: calc(100% - 2rem);
          padding: 0.62rem 0.72rem 0.62rem 0.9rem;
          border: 1px solid rgba(157, 255, 99, 0.32);
          border-radius: 0.72rem;
          display: flex;
          align-items: center;
          gap: 0.8rem;
          pointer-events: auto;
          color: rgba(245, 247, 242, 0.82);
          font-size: 0.62rem;
          letter-spacing: 0.1em;
          white-space: nowrap;
        }

        .testcs-entry-hint strong {
          color: var(--testcs-accent);
        }

        .testcs-entry-hint button {
          appearance: none;
          width: 1.6rem;
          height: 1.6rem;
          border: 1px solid var(--testcs-border);
          border-radius: 0.45rem;
          background: rgba(255, 255, 255, 0.04);
          color: rgba(245, 247, 242, 0.72);
          cursor: pointer;
        }

        .testcs-bottom {
          position: absolute;
          left: 50%;
          bottom: calc(1.25rem + env(safe-area-inset-bottom));
          transform: translateX(-50%);
          display: flex;
          align-items: flex-end;
          gap: 0.55rem;
        }

        .testcs-nav {
          /* референс-раскладка: мир во весь экран, две пилюли-дока по углам */
          display: flex;
          align-items: stretch;
          justify-content: space-between;
          gap: 0.6rem;
          flex: 1;
          background: transparent;
          border: none;
          pointer-events: none;
        }

        .testcs-dock {
          min-height: 4.45rem;
          padding: 0.45rem;
          display: flex;
          align-items: stretch;
          gap: 0.25rem;
          border-radius: 999px;
          pointer-events: auto;
        }

        .testcs-nav-btn {
          width: 5.4rem;
          min-height: 3.5rem;
          padding: 0.42rem 0.35rem 0.35rem;
          appearance: none;
          background: transparent;
          border: 1px solid transparent;
          border-radius: 0.7rem;
          color: rgba(245, 247, 242, 0.66);
          font: inherit;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 0.34rem;
          transition:
            color 140ms ease,
            background 140ms ease,
            border-color 140ms ease,
            transform 140ms ease;
          user-select: none;
          position: relative;
          svg {
            width: 1.18rem;
            height: 1.18rem;
          }
          span {
            font-size: 0.54rem;
            font-weight: 650;
            letter-spacing: 0.1em;
          }
          /* клавиша в углу кнопки: подсказка видна там же, где действие */
          .testcs-nav-key {
            position: absolute;
            top: 0.28rem;
            right: 0.32rem;
            padding: 0.05rem 0.24rem;
            border-radius: 0.28rem;
            background: rgba(255, 255, 255, 0.08);
            color: rgba(245, 247, 242, 0.5);
            font-size: 0.42rem;
            font-style: normal;
            font-weight: 700;
            letter-spacing: 0.06em;
            line-height: 1.5;
          }
          &:hover {
            color: white;
            background: rgba(255, 255, 255, 0.06);
            cursor: pointer;
            transform: translateY(-1px);
            .testcs-nav-key {
              background: rgba(255, 255, 255, 0.16);
              color: rgba(245, 247, 242, 0.82);
            }
          }
          &.active {
            color: var(--testcs-accent);
            background: var(--testcs-accent-soft);
            border-color: rgba(157, 255, 99, 0.35);
          }
          &.unavailable {
            opacity: 0.42;
          }
        }

        .testcs-voice {
          width: 4.45rem;
          height: 4.45rem;
          appearance: none;
          border-radius: 1rem;
          font: inherit;
          color: rgba(245, 247, 242, 0.72);
          display: flex;
          align-items: center;
          justify-content: center;
          pointer-events: auto;
          transition:
            color 140ms ease,
            border-color 140ms ease;
          &:hover {
            color: white;
            border-color: rgba(157, 255, 99, 0.35);
            cursor: pointer;
          }
          &.muted {
            color: #ff8473;
          }
          &.disabled {
            color: rgba(245, 247, 242, 0.24);
            pointer-events: none;
          }
        }

        .testcs-help {
          position: absolute;
          right: calc(1.25rem + env(safe-area-inset-right));
          bottom: calc(1.25rem + env(safe-area-inset-bottom));
          height: 2rem;
          padding: 0 0.7rem;
          border-radius: 0.6rem;
          display: flex;
          align-items: center;
          gap: 0.55rem;
          color: rgba(245, 247, 242, 0.55);
          font-size: 0.54rem;
          letter-spacing: 0.1em;
          kbd {
            color: rgba(245, 247, 242, 0.86);
            font-family: inherit;
          }
        }

        @media all and (max-width: 760px) {
          .testcs-topbar {
            top: calc(0.7rem + env(safe-area-inset-top));
            left: calc(0.7rem + env(safe-area-inset-left));
            right: calc(0.7rem + env(safe-area-inset-right));
          }
          .testcs-brand {
            min-width: 0;
            min-height: 3.2rem;
            padding: 0.55rem 0.65rem;
          }
          .testcs-brand-meta,
          .testcs-session-main {
            display: none;
          }
          .testcs-session {
            min-height: 3.2rem;
            padding: 0.55rem 0.7rem;
          }
          .testcs-online {
            border-left: 0;
            padding-left: 0;
          }
          .testcs-bottom {
            bottom: calc(0.7rem + env(safe-area-inset-bottom));
            width: calc(100% - 1.4rem - env(safe-area-inset-left) - env(safe-area-inset-right));
          }
          .testcs-nav {
            min-width: 0;
            flex: 1;
            min-height: 3.8rem;
            padding: 0.32rem;
            overflow-x: auto;
            -webkit-overflow-scrolling: touch;
            scrollbar-width: none;
          }
          .testcs-nav::-webkit-scrollbar {
            display: none;
          }
          .testcs-nav-btn {
            /* кнопки не сжимаются в кашу — панель листается пальцем */
            width: 4.1rem;
            flex: 0 0 4.1rem;
            min-height: 3.1rem;
            span {
              font-size: 0.5rem;
            }
          }
          .testcs-voice {
            width: 3.8rem;
            height: 3.8rem;
            flex: 0 0 3.8rem;
          }
          .testcs-help {
            display: none;
          }
          .testcs-objective {
            top: calc(4.45rem + env(safe-area-inset-top));
            min-width: 0;
            width: calc(100% - 5rem);
            font-size: 0.48rem;
          }
          .testcs-quickpane {
            width: calc(100vw - 1.2rem) !important;
            max-width: calc(100vw - 1.2rem) !important;
            max-height: 52vh;
            overflow-y: auto;
          }
          .testcs-quickpane-btn {
            min-height: 2.75rem; /* палец */
          }
          .testcs-entry-hint {
            top: calc(6.3rem + env(safe-area-inset-top));
            width: calc(100% - 1.4rem);
            justify-content: space-between;
            white-space: normal;
            font-size: 0.55rem;
          }
        }
      `}
    >
      <header className='testcs-topbar'>
        <div className='testcs-brand'>
          <div className='testcs-mark' aria-hidden='true' />
          <div className='testcs-brand-copy'>
            <div className='testcs-brand-title'>TESTCS WORLD</div>
            <div className='testcs-brand-meta'>ЖИВОЙ МИР // ОСТРОВА</div>
          </div>
        </div>
        <div className='testcs-session'>
          <div className='testcs-session-main'>
            <div className='testcs-session-world'>{locationCopy.world}</div>
            <div className='testcs-session-id'>{locationCopy.worldId}</div>
          </div>
          <div className='testcs-online'>
            <strong>{online}</strong>
            <span>● ONLINE</span>
          </div>
        </div>
      </header>

      {objective && (
        <div className='testcs-objective' role='status' aria-label={`Цель: ${objective.label}, ${objective.distance} метров`}>
          <span className='testcs-objective-arrow' style={{ transform: `rotate(${objectiveAngleDeg}deg)` }} aria-hidden='true'>
            ▲
          </span>
          <span className='testcs-objective-copy'>
            <strong>{objective.label}</strong>
            <small>{objective.kind} · {objective.distance} м</small>
          </span>
        </div>
      )}

      {entryHintVisible && (
        <div className='testcs-entry-hint' data-testid='player-entry-hint' role='status'>
          <span>
            <strong>{locationCopy.world}</strong> · {isTouch ? 'ДВИЖЕНИЕ — КАСАНИЕМ · АРКИ — ПОРТАЛЫ' : 'WASD — ИДТИ · ESC — МЕНЮ · АРКИ — ПОРТАЛЫ'}
          </span>
          <button type='button' aria-label='Закрыть подсказку' onClick={() => setEntryHintVisible(false)}>
            ×
          </button>
        </div>
      )}

      {quickPane === 'emotes' && <EmoteWheelScreen world={world} onClose={() => setQuickPane(null)} />}

      {quickPane === 'commands' && (
        <CommandPalette world={world} initialQuery={paletteQuery} onClose={() => { setPaletteQuery(''); setQuickPane(null) }} />
      )}

      {(quickPane === 'presence' || quickPane === 'map') && <SocialMapScreen
        world={world}
        initialTab={quickPane === 'map' ? 'map' : 'social'}
        onClose={() => setQuickPane(null)}
        onOpenChat={userId => { setChatTargetUserId(userId); setQuickPane('chatscreen') }}
        onOpenParty={() => setQuickPane('party')}
      />}

      {quickPane === 'graphics' && (
        <div className='testcs-quickpane' role='menu' aria-label='Качество графики'>
          <div className='testcs-quickpane-title'>
            ГРАФИКА · {graphics ? TIER_LABELS[graphics.tier] : '…'}
            {graphics?.auto ? ' (авто)' : ''}
          </div>
          <div className='testcs-quickpane-grid'>
            {TIERS.map(tier => (
              <button
                key={tier}
                type='button'
                className={cls('testcs-quickpane-btn', { active: graphics?.tier === tier })}
                onClick={() => {
                  setGraphicsTier(world, tier)
                  setGraphics({ tier, reason: 'выбрано вручную', auto: false })
                }}
              >
                {TIER_LABELS[tier]}
              </button>
            ))}
          </div>
          {graphics?.reason && (
            <div style={{ padding: '6px 10px', fontSize: 11, opacity: 0.7 }}>Почему: {graphics.reason}</div>
          )}
        </div>
      )}

      {quickPane === 'chatscreen' && <ChatScreen world={world} initialTargetUserId={chatTargetUserId} onClose={() => { setChatTargetUserId(''); setQuickPane(null) }} />}

      {quickPane === 'art' && (
        <div className='testcs-quickpane' role='menu' aria-label='Арт-режим оболочки'>
          <div className='testcs-quickpane-title'>СТИЛЬ · ОБОЛОЧКА ИНТЕРФЕЙСА</div>
          <div className='testcs-quickpane-grid'>
            {ART_MODES.map(mode => (
              <button
                key={mode.id}
                type='button'
                className={cls('testcs-quickpane-btn', { active: artMode === mode.id })}
                title={mode.hint}
                onClick={() => setArtModeState(setArtMode(mode.id))}
              >
                {mode.label}
              </button>
            ))}
          </div>
          <div style={{ padding: '0 10px 10px', fontSize: 11, opacity: 0.65 }}>
            {ART_MODES.find(mode => mode.id === artMode)?.hint}
          </div>
        </div>
      )}

      {quickPane === 'voice' && <VoiceScreen world={world} onClose={() => setQuickPane(null)} />}

      {quickPane === 'voicehealth' && <VoiceHealthScreen world={world} onClose={() => setQuickPane(null)} />}

      {quickPane === 'quests' && <QuestJournalScreen world={world} onClose={() => setQuickPane(null)} />}

      {quickPane === 'party' && <PartyLobbyScreen world={world} onClose={() => setQuickPane(null)} />}

      {quickPane === 'security' && <GuestSecurityScreen world={world} onClose={() => setQuickPane(null)} />}

      {quickPane === 'wallet' && <WalletPlotsScreen world={world} initialTab='wallet' onClose={() => setQuickPane(null)} />}

      {quickPane === 'plots' && <WalletPlotsScreen world={world} initialTab='plots' onClose={() => setQuickPane(null)} />}

      {quickPane === 'inventory' && <InventoryScreen world={world} onClose={() => setQuickPane(null)} />}

      {quickPane === 'settings' && <SettingsScreen world={world} onClose={() => setQuickPane(null)} onGraphicsChange={setGraphics} onArtModeChange={setArtModeState} />}

      {quickPane === 'companion' && <MiaCompanionScreen world={world} onClose={() => setQuickPane(null)} />}

      {quickPane === 'marketplace' && <MarketplaceScreen world={world} onClose={() => setQuickPane(null)} />}
      {quickPane === 'economy' && <EconomyScreen world={world} onClose={() => setQuickPane(null)} />}

      {quickPane === 'admin' && adminAllowed && (
        <div className='testcs-quickpane' role='menu' aria-label='Админка'>
          <div className='testcs-quickpane-title'>АДМИН · ОСТРОВА И ИГРОКИ</div>
          <div className='testcs-quickpane-grid'><button type='button' className='testcs-quickpane-btn' onClick={() => setQuickPane('map')}>ОТКРЫТЬ СЕРВЕРНУЮ КАРТУ</button></div>
          <div style={{ padding: '6px 10px', fontSize: 11, opacity: 0.7 }}>
            Ранги и имена — командами /admin, /name. Нулевой аккаунт для теста: адрес ?fresh
          </div>
        </div>
      )}

      {quickPane === 'more' && (
        <div className='testcs-quickpane' role='menu' aria-label='Ещё'>
          <div className='testcs-quickpane-title'>ЕЩЁ · МИР И НАСТРОЙКИ</div>
          <div className='testcs-quickpane-grid'>
            <button type='button' className='testcs-quickpane-btn' onClick={() => { setQuickPane(null); togglePane('world') }}>
              МИР
            </button>
            <button type='button' className='testcs-quickpane-btn' onClick={() => setQuickPane('commands')}>
              КОМАНДЫ
            </button>
            <button type='button' className='testcs-quickpane-btn' onClick={() => setQuickPane('grid')}>
              ГРИД
            </button>
            <button type='button' className='testcs-quickpane-btn' onClick={() => setQuickPane('quests')}>
              ЗАДАНИЯ
            </button>
            <button type='button' className='testcs-quickpane-btn' onClick={() => setQuickPane('companion')}>
              MIA · КОНТРОЛЬ
            </button>
            <button type='button' className='testcs-quickpane-btn' onClick={() => setQuickPane('party')}>
              ГРУППА
            </button>
            <button type='button' className='testcs-quickpane-btn' onClick={() => setQuickPane('security')}>
              БЕЗОПАСНОСТЬ
            </button>
            <button type='button' className='testcs-quickpane-btn' onClick={() => setQuickPane('wallet')}>
              КОШЕЛЁК
            </button>
            <button type='button' className='testcs-quickpane-btn' onClick={() => setQuickPane('plots')}>
              УЧАСТКИ
            </button>
            <button type='button' className='testcs-quickpane-btn' onClick={() => setQuickPane('inventory')}>
              ИНВЕНТАРЬ
            </button>
            <button type='button' className='testcs-quickpane-btn' onClick={() => setQuickPane('economy')}>
              РЫНОК ГОРОДА
            </button>
            <button type='button' className='testcs-quickpane-btn' onClick={() => setQuickPane('marketplace')}>
              МАРКЕТПЛЕЙС
            </button>
            <button type='button' className='testcs-quickpane-btn' onClick={() => setQuickPane('map')}>
              КАРТА
            </button>
            <button type='button' className='testcs-quickpane-btn' onClick={() => setQuickPane('graphics')}>
              ГРАФИКА
            </button>
            <button type='button' className='testcs-quickpane-btn' onClick={() => setQuickPane('art')}>
              СТИЛЬ
            </button>
            <button type='button' className='testcs-quickpane-btn' onClick={() => setQuickPane('voice')}>
              ГОЛОС
            </button>
            <button type='button' className='testcs-quickpane-btn' onClick={() => setQuickPane('voicehealth')}>
              ДИАГНОСТИКА
            </button>
            <button type='button' className='testcs-quickpane-btn' onClick={() => { setQuickPane(null); togglePane('prefs') }}>
              СИСТЕМА
            </button>
            <button type='button' className='testcs-quickpane-btn' onClick={() => setQuickPane('settings')}>
              НАСТРОЙКИ
            </button>
          </div>
        </div>
      )}

      {quickPane === 'avatar' && (
        <div className='testcs-quickpane' role='menu' aria-label='Выбор персонажа'>
          <div className='testcs-quickpane-title'>ПЕРСОНАЖ · ВЫБОР СОХРАНЯЕТСЯ</div>
          <div className='testcs-quickpane-grid'>
            {(world.network?.avatarGallery || []).map((item, index) => (
              <button
                key={item.url}
                type='button'
                className={cls('testcs-quickpane-btn', {
                  active: world.entities?.player?.data?.avatar === item.url,
                })}
                title={item.starter ? 'Стартовый персонаж' : 'Персонаж галереи'}
                onClick={() => runGridCommand(['avatar', 'set', String(index + 1)])}
              >
                {item.name || `#${index + 1}`}
              </button>
            ))}
          </div>
          {!(world.network?.avatarGallery || []).length && (
            <div style={{ padding: '6px 10px', fontSize: 11, opacity: 0.7 }}>Галерея пуста — пул не установлен.</div>
          )}
          <div className='testcs-quickpane-title'>СВОЙ ПЕРСОНАЖ · ССЫЛКА НА VRM</div>
          <form
            style={{ display: 'flex', gap: 6, padding: '0 10px 10px' }}
            onSubmit={event => {
              event.preventDefault()
              const url = event.target.elements.avatarUrl.value.trim()
              if (!url) return
              runGridCommand(['avatar', 'url', url])
              event.target.reset()
            }}
          >
            <input
              name='avatarUrl'
              type='url'
              placeholder='https://…/model.vrm'
              autoComplete='off'
              style={{
                flex: 1,
                minHeight: 40,
                fontSize: 16,
                padding: '0 10px',
                borderRadius: 8,
                border: '1px solid rgba(255,255,255,0.16)',
                background: 'rgba(0,0,0,0.35)',
                color: 'inherit',
              }}
            />
            <button type='submit' className='testcs-quickpane-btn' style={{ minWidth: 90 }}>
              НАДЕТЬ
            </button>
          </form>
          <div style={{ padding: '0 10px 10px', fontSize: 11, opacity: 0.65 }}>
            Сделай своего в Ready Player Me или VRoid Hub и вставь прямую ссылку на .vrm
          </div>
        </div>
      )}

      {quickPane === 'grid' && (
        <div className='testcs-quickpane' role='menu' aria-label='Команды грида'>
          <div className='testcs-quickpane-title'>ГРИД · ОТВЕТЫ В ЧАТЕ</div>
          <div className='testcs-quickpane-grid'>
            {HUD_GRID_COMMANDS.map(command => (
              <button
                key={command.id}
                type='button'
                className='testcs-quickpane-btn'
                title={`/${command.args.join(' ')}`}
                onClick={() => runGridCommand(command.args)}
              >
                {command.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {worldContext && !quickPane && !buildOpen && (
        <div
          className='testcs-quickpane'
          role='status'
          aria-label='Рядом с тобой'
          style={{ width: 300, maxWidth: '90vw' }}
        >
          <div className='testcs-quickpane-title'>{contextTitle(worldContext)}</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, padding: '6px 10px 10px' }}>
            {contextActions(worldContext).map(action => (
              <button
                key={action.id}
                type='button'
                className='testcs-quickpane-btn'
                style={{ padding: '4px 10px', fontSize: 11 }}
                onClick={() => {
                  if (action.query) {
                    setPaletteQuery(action.query)
                    setQuickPane('commands')
                    return
                  }
                  runGridCommand(action.args)
                }}
              >
                {action.label}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className='testcs-bottom'>
        <nav className='testcs-nav' aria-label='Основная навигация TESTCS'>
          {/* левая пилюля — социальное: как на референсах вижена */}
          <div className='testcs-dock' role='group' aria-label='Общение'>
            <HudButton active={quickPane === 'presence'} label='ЛЮДИ' hotkey='P' onClick={() => toggleQuickPane('presence')}>
              <UsersIcon />
            </HudButton>
            <HudButton active={quickPane === 'chatscreen'} label='ЧАТ' hotkey='ENTER' onClick={() => toggleQuickPane('chatscreen')}>
              <MessageSquareTextIcon />
            </HudButton>
            <HudButton active={quickPane === 'emotes'} label='ЭМОЦИИ' hotkey='G' onClick={() => toggleQuickPane('emotes')}>
              <Music4Icon />
            </HudButton>
          </div>
          {/* правая пилюля — я и мир */}
          <div className='testcs-dock' role='group' aria-label='Действия'>
            <HudButton active={quickPane === 'avatar'} label='ПЕРСОНАЖ' hotkey='C' onClick={() => toggleQuickPane('avatar')}>
              <UsersIcon />
            </HudButton>
            <HudButton active={quickPane === 'map'} label='КАРТА' hotkey='M' onClick={() => toggleQuickPane('map')}>
              <MapPinIcon />
            </HudButton>
            <HudButton active={buildOpen} unavailable={!buildAllowed} label='СОЗДАТЬ' hotkey='B' onClick={toggleBuild}>
              <HammerIcon />
            </HudButton>
            {adminAllowed && (
              <HudButton active={quickPane === 'admin'} label='АДМИН' onClick={() => toggleQuickPane('admin')}>
                <SettingsIcon />
              </HudButton>
            )}
            <HudButton active={quickPane === 'more'} label='ЕЩЁ' hotkey='U' onClick={() => toggleQuickPane('more')}>
              <SettingsIcon />
            </HudButton>
          </div>
        </nav>

        {voice.available && (
          <button
            type='button'
            className={cls('testcs-voice', {
              muted: voice.mic && (voice.level === 'disabled' || voice.muted),
              disabled: !voice.connected,
            })}
            aria-label={
              voice.mic && voice.level !== 'disabled' && !voice.muted ? 'Выключить микрофон' : 'Включить микрофон'
            }
            onClick={() => world.livekit.setMicrophoneEnabled()}
          >
            {voice.mic && voice.level !== 'disabled' && !voice.muted ? <MicIcon /> : <MicOffIcon />}
          </button>
        )}
      </div>

      {!isTouch && (
        <div className='testcs-help'>
          <span>
            <kbd>WASD</kbd> ДВИЖЕНИЕ
          </span>
          <span>
            <kbd>ENTER</kbd> ЧАТ
          </span>
          <span>
            <kbd>SPACE</kbd> ПРЫЖОК
          </span>
          <span>
            <kbd>SHIFT</kbd> БЕГ
          </span>
          {buildAllowed && (
            <span>
              <kbd>B</kbd> ДОМ
            </span>
          )}
        </div>
      )}

      {buildOpen && (
        <BuildMode
          world={world}
          open={buildOpen}
          onClose={() => setBuildOpen(false)}
          adapter={buildAdapter}
          canBuild={buildAllowed}
        />
      )}
    </div>
  )
}

// hotkey — подпись клавиши прямо на кнопке: «непонятно, что нажимать» была
// прямой претензией владельца к управлению с ПК (2026-08-05).
function HudButton({ active, unavailable, label, hotkey, children, onClick }) {
  return (
    <button
      type='button'
      className={cls('testcs-nav-btn', { active, unavailable })}
      aria-disabled={unavailable || undefined}
      aria-keyshortcuts={hotkey || undefined}
      onClick={onClick}
    >
      {hotkey && <i className='testcs-nav-key' aria-hidden='true'>{hotkey}</i>}
      {children}
      <span>{label}</span>
    </button>
  )
}

// Карта региона (аналог World Map в SL): участки, игроки, объекты игроков.
// Клик по карте — телепорт в точку, как в вьюере OpenSim.
const MAP_BOUNDS = { minX: -120, maxX: 120, minZ: -300, maxZ: -140 }
const MAP_SIZE = 320

function RegionMap({ world, onClose }) {
  const [tick, setTick] = useState(0)
  const [query, setQuery] = useState('')
  useEffect(() => {
    const timer = setInterval(() => setTick(value => value + 1), 500)
    return () => clearInterval(timer)
  }, [])

  const toMapPct = (x, z) => ({
    left: ((x - MAP_BOUNDS.minX) / (MAP_BOUNDS.maxX - MAP_BOUNDS.minX)) * 100,
    top: ((z - MAP_BOUNDS.minZ) / (MAP_BOUNDS.maxZ - MAP_BOUNDS.minZ)) * 100,
  })
  const toWorld = (left, top, size) => [
    MAP_BOUNDS.minX + (left / size) * (MAP_BOUNDS.maxX - MAP_BOUNDS.minX),
    MAP_BOUNDS.minZ + (top / size) * (MAP_BOUNDS.maxZ - MAP_BOUNDS.minZ),
  ]

  const localPlayer = world.entities?.player
  const plots = world.blueprints?.get?.('testcs-build-authority')?.props?.plotOrigins || []
  const players = []
  world.entities?.players?.forEach?.(entity => {
    const position = entity?.base?.position || entity?.root?.position
    if (position) players.push({ id: entity.data?.id, name: entity.data?.name || 'Гость', position })
  })
  const objects = []
  world.entities?.items?.forEach?.(entity => {
    if (!String(entity.blueprint?.id || '').startsWith('grid-prop-')) return
    const position = entity.root?.position
    if (position) objects.push({ x: position.x, z: position.z })
  })

  const mapIndex = buildMapIndex({ places: HUD_PLACES, players, plots, selfId: localPlayer?.data?.id })
  const results = searchMap(query, mapIndex)

  const goTo = () => world.ui.notify({ kind: 'warning', title: 'КАРТА ОБНОВЛЕНА', message: 'Используй live-карту: локальные координатные переходы отключены.', dedupeKey: 'legacy-map-disabled' })
  const jump = event => {
    const rect = event.currentTarget.getBoundingClientRect()
    const [x, z] = toWorld(event.clientX - rect.left, event.clientY - rect.top, rect.width)
    void x
    void z
    world.ui.notify({ kind: 'warning', title: 'КООРДИНАТНЫЙ TP ЗАКРЫТ', message: 'Клиент не может назначать координаты.', dedupeKey: 'coordinate-teleport-disabled' })
  }

  return (
    <div
      role='dialog'
      aria-label='Карта региона'
      data-tick={tick}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 60,
        background: 'rgba(3,6,10,0.92)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: 'max(10px, env(safe-area-inset-top)) 12px 12px',
        gap: 10,
      }}
    >
      <div style={{ display: 'flex', width: 'min(680px, 96vw)', gap: 8, alignItems: 'center' }}>
        <input
          value={query}
          onChange={event => setQuery(event.target.value)}
          placeholder='спавн, имя игрока, участок…'
          aria-label='Поиск по карте'
          style={{
            flex: 1,
            padding: '12px 14px',
            fontSize: 16,
            background: 'rgba(10,16,24,0.95)',
            border: '1px solid rgba(120,200,255,0.35)',
            color: '#e8f4ff',
            outline: 'none',
          }}
        />
        <button
          type='button'
          onClick={onClose}
          aria-label='Закрыть карту'
          style={{
            minWidth: 44,
            minHeight: 44,
            fontSize: 20,
            background: 'rgba(10,16,24,0.95)',
            border: '1px solid rgba(120,200,255,0.35)',
            color: '#e8f4ff',
            cursor: 'pointer',
          }}
        >
          ×
        </button>
      </div>

      <div
        onClick={jump}
        style={{
          position: 'relative',
          width: 'min(78vw, 52vh)',
          height: 'min(78vw, 52vh)',
          background: 'rgba(6,10,16,0.95)',
          border: '1px solid rgba(120,200,255,0.35)',
          cursor: 'crosshair',
          overflow: 'hidden',
          flexShrink: 0,
        }}
      >
        {plots.map((origin, index) => {
          if (!Array.isArray(origin)) return null
          const topLeft = toMapPct(origin[0] - 12, origin[2] - 12)
          const bottomRight = toMapPct(origin[0] + 12, origin[2] + 12)
          return (
            <div
              key={index}
              title={'Участок #' + index}
              style={{
                position: 'absolute',
                left: topLeft.left + '%',
                top: topLeft.top + '%',
                width: Math.max(1.5, bottomRight.left - topLeft.left) + '%',
                height: Math.max(1.5, bottomRight.top - topLeft.top) + '%',
                border: '1px solid rgba(120,200,255,0.5)',
                background: 'rgba(120,200,255,0.08)',
              }}
            />
          )
        })}
        {objects.map((object, index) => {
          const point = toMapPct(object.x, object.z)
          return (
            <div
              key={'o' + index}
              style={{
                position: 'absolute',
                left: 'calc(' + point.left + '% - 2px)',
                top: 'calc(' + point.top + '% - 2px)',
                width: 4,
                height: 4,
                background: '#ffb257',
              }}
            />
          )
        })}
        {players.map(player => {
          const isSelf = player.id === localPlayer?.data?.id
          const point = toMapPct(player.position.x, player.position.z)
          return (
            <div
              key={player.id}
              title={player.name}
              style={{
                position: 'absolute',
                left: 'calc(' + point.left + '% - 4px)',
                top: 'calc(' + point.top + '% - 4px)',
                width: 8,
                height: 8,
                borderRadius: '50%',
                background: isSelf ? '#2fd2ff' : '#ff2f8e',
                boxShadow: isSelf ? '0 0 10px #2fd2ff' : 'none',
              }}
            />
          )
        })}
      </div>

      <div style={{ width: 'min(680px, 96vw)', flex: 1, overflowY: 'auto', minHeight: 0 }}>
        {results.slice(0, 30).map(row => (
          <button
            key={row.id}
            type='button'
            onClick={() => goTo(row)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              width: '100%',
              minHeight: 44,
              padding: '8px 12px',
              background: 'transparent',
              border: 'none',
              borderBottom: '1px solid rgba(120,200,255,0.12)',
              color: '#dbe9f5',
              cursor: 'pointer',
              textAlign: 'left',
            }}
          >
            <span style={{ fontSize: 14 }}>{row.label}</span>
            <span style={{ fontSize: 11, opacity: 0.5 }}>{row.hint}</span>
            <span style={{ marginLeft: 'auto', fontSize: 11, opacity: 0.4 }}>перейти →</span>
          </button>
        ))}
        {!results.length && <div style={{ padding: 12, opacity: 0.6, fontSize: 13 }}>Ничего не нашлось</div>}
      </div>
    </div>
  )
}

// Командная палитра: вместо запоминания слэш-команд — поиск по-русски.
// Открывается кнопкой КОМАНДЫ или клавишей «/», выбор мышью или клавишами.
function CommandPalette({ world, onClose, initialQuery = '' }) {
  const [query, setQuery] = useState(initialQuery)
  const [cursor, setCursor] = useState(0)
  const inputRef = useRef(null)
  const results = searchCommands(query)
  const groups = groupByCategory(results)
  const flat = groups.flatMap(group => group.commands)
  const active = flat[Math.min(cursor, Math.max(0, flat.length - 1))]

  useEffect(() => {
    inputRef.current?.focus()
  }, [])
  useEffect(() => {
    setCursor(0)
  }, [query])

  const run = command => {
    if (!command) return
    // команда с аргументом — дописываем её в строку, а не запускаем вслепую
    if (command.needsInput && !query.includes(' ')) {
      setQuery(`${command.cmd} `)
      inputRef.current?.focus()
      return
    }
    const tail = query.toLowerCase().startsWith(command.cmd.toLowerCase())
      ? query.slice(command.cmd.length).trim()
      : ''
    const args = toArgs(command, tail)
    world.network?.send('command', { cmd: args[0], value: args.slice(1).join(' '), args, requestId: uuid() })
    world.emit('sidebar-chat-toggle')
    onClose?.()
  }

  const onKeyDown = event => {
    if (event.key === 'Escape') return onClose?.()
    if (event.key === 'ArrowDown') { event.preventDefault(); setCursor(value => Math.min(value + 1, flat.length - 1)) }
    if (event.key === 'ArrowUp') { event.preventDefault(); setCursor(value => Math.max(value - 1, 0)) }
    if (event.key === 'Enter') { event.preventDefault(); run(active) }
  }

  return (
    <div
      className='testcs-quickpane'
      role='dialog'
      aria-label='Команды мира'
      style={{ width: 420, maxWidth: '92vw', maxHeight: '60vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}
    >
      <div className='testcs-quickpane-title'>КОМАНДЫ · ПИШИ ПО-РУССКИ</div>
      <input
        ref={inputRef}
        value={query}
        onChange={event => setQuery(event.target.value)}
        onKeyDown={onKeyDown}
        placeholder='дом, вещи, земля, деньги, погода…'
        aria-label='Поиск команды'
        style={{
          margin: '8px 10px',
          padding: '8px 10px',
          background: 'rgba(8,14,20,0.9)',
          border: '1px solid rgba(120,200,255,0.35)',
          color: '#e8f4ff',
          outline: 'none',
          fontSize: 16,
        }}
      />
      <div style={{ overflowY: 'auto', padding: '0 4px 8px' }}>
        {!flat.length && <div style={{ padding: '8px 12px', opacity: 0.6, fontSize: 12 }}>Ничего не нашлось</div>}
        {groups.map(group => (
          <div key={group.id}>
            <div style={{ padding: '6px 12px 2px', fontSize: 10, letterSpacing: 1, opacity: 0.5 }}>{group.label}</div>
            {group.commands.map(command => {
              const index = flat.indexOf(command)
              return (
                <button
                  key={command.id}
                  type='button'
                  onMouseEnter={() => setCursor(index)}
                  onClick={() => run(command)}
                  style={{
                    display: 'block',
                    width: '100%',
                    textAlign: 'left',
                    padding: '7px 12px',
                    background: index === cursor ? 'rgba(47,210,255,0.16)' : 'transparent',
                    border: 'none',
                    borderLeft: index === cursor ? '2px solid #2fd2ff' : '2px solid transparent',
                    color: '#dbe9f5',
                    cursor: 'pointer',
                  }}
                >
                  <span style={{ fontSize: 13 }}>{command.label}</span>
                  <span style={{ fontSize: 11, opacity: 0.55, marginLeft: 8 }}>/{command.cmd}</span>
                  {command.needsInput && (
                    <span style={{ fontSize: 10, opacity: 0.45, marginLeft: 6 }}>· {command.needsInput}</span>
                  )}
                  <div style={{ fontSize: 11, opacity: 0.6 }}>{command.desc}</div>
                </button>
              )
            })}
          </div>
        ))}
      </div>
    </div>
  )
}

// Панель присутствия: ИИ-жители наверху, живые игроки по близости, действия
// в один клик — подойти, написать, заговорить, в друзья.
function PresencePanel({ world, onCommand, onClose }) {
  const [tick, setTick] = useState(0)
  useEffect(() => {
    const timer = setInterval(() => setTick(value => value + 1), 800)
    return () => clearInterval(timer)
  }, [])

  const localPlayer = world.entities?.player
  const players = []
  world.entities?.players?.forEach?.(entity => {
    const position = entity?.base?.position || entity?.root?.position
    players.push({ id: entity.data?.id, name: entity.data?.name, position })
  })
  const rows = buildPresenceList(players, {
    localId: localPlayer?.data?.id,
    origin: localPlayer?.base?.position,
  })

  const run = (row, action) => {
    if (action.chat) {
      // «заговорить» — обычная реплика в чат: ИИ-житель слышит её как игрока
      world.chat.add({ id: uuid(), from: localPlayer?.data?.name || null, fromId: localPlayer?.data?.id || null, body: action.chat, createdAt: new Date().toISOString() }, true)
      world.emit('sidebar-chat-toggle')
      onClose?.()
      return
    }
    if (action.needsInput) {
      // сообщение без текста не отправляем — открываем чат с заготовкой
      world.emit('sidebar-chat-toggle')
      world.chat.add({ id: uuid(), from: null, fromId: null, body: `Напиши: /im ${row.name} <текст>`, createdAt: new Date().toISOString() }, false)
      onClose?.()
      return
    }
    onCommand(action.args)
  }

  return (
    <div className='testcs-quickpane' role='menu' aria-label='Кто в мире' data-tick={tick} style={{ width: 320, maxWidth: '92vw' }}>
      <div className='testcs-quickpane-title'>КТО В МИРЕ · {rows.length}</div>
      <div style={{ maxHeight: '46vh', overflowY: 'auto' }}>
        {rows.map(row => (
          <div
            key={row.id}
            style={{
              padding: '8px 12px',
              borderLeft: row.ai ? '2px solid #2fd2ff' : '2px solid transparent',
              background: row.ai ? 'rgba(47,210,255,0.08)' : 'transparent',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
              <span style={{ fontSize: 13, color: row.isSelf ? '#8fa6b8' : '#e8f4ff' }}>{row.name}</span>
              {row.ai && <span style={{ fontSize: 10, opacity: 0.6 }}>{row.ai.role}</span>}
              <span style={{ marginLeft: 'auto', fontSize: 11, opacity: 0.5 }}>
                {row.isSelf ? 'это ты' : Number.isFinite(row.meters) ? `${row.meters} м` : '—'}
              </span>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 }}>
              {presenceActions(row).map(action => (
                <button
                  key={action.id}
                  type='button'
                  className='testcs-quickpane-btn'
                  style={{ padding: '3px 8px', fontSize: 11 }}
                  onClick={() => run(row, action)}
                >
                  {action.label}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
